import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import StatCounter from "../components/StatCounter";
import CategoryGrid from "../components/CategoryGrid";
import CallToActionBanner from "../components/CallToActionBanner";
import PartnerMarquee from "../components/PartnerMarquee";
import QuoteForm from "../components/QuoteForm";
import HeroCarousel from "../components/HeroCarousel";
import { api } from "../lib/api";
import Editable from "../components/Editable";
import CountriesModal from "../components/CountriesModal";
import MaterialHub from "../components/MaterialHub";
import { company, services, strengths, stats, glanceStats, news, whatWeDoIntro, glanceContent, NEWS_ENABLED } from "../mock";
import SEO from "../components/SEO";

function GlanceSection({ onLearnMore }) {
  return (
    <section id="glance" className="section-padding bg-white scroll-mt-28">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <div className="fade-in">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold mb-3" style={{ color: "var(--brand-500)" }}>
            <Editable field="glance.overline" as="span">{glanceContent.overline}</Editable>
          </p>
          <Editable field="glance.headline" as="h2" className="font-display text-2xl md:text-4xl font-semibold leading-tight" style={{ color: "var(--brand-700)" }}>
            {glanceContent.headline}
          </Editable>
          <Editable field="glance.subheadline" as="p" className="mt-4 text-lg font-medium text-foreground/80" multiline>{glanceContent.subheadline}</Editable>
          <Editable field="glance.p1" as="p" className="mt-6 text-muted-foreground text-[15px] leading-relaxed" multiline>{glanceContent.p1}</Editable>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {glanceStats.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Globe2;
              return (
                <div key={s.label} className="p-5 rounded-xl border border-border bg-muted/30">
                  <Icon size={24} className="mb-3" style={{ color: "var(--brand-500)" }} />
                  <div className="font-display font-bold text-2xl" style={{ color: "var(--brand-700)" }}>
                    {s.prefix || ""}{s.value}{s.suffix || ""}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground"><Editable field={`facts.${i}.label`} as="span">{s.label}</Editable></div>
                </div>
              );
            })}
          </div>
          <button onClick={onLearnMore} className="mt-8 inline-flex items-center gap-2 font-semibold link-underline" style={{ color: "var(--brand-700)" }}>
            Learn More <Icons.ArrowRight size={16} />
          </button>
        </div>
        <div className="relative fade-in">
          <div className="absolute -top-6 -left-6 w-32 h-32 rounded-xl" style={{ background: "var(--brand-100)" }} />
          <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-xl border-4" style={{ borderColor: "var(--brand-500)" }} />
          <img
            src={glanceContent.image}
            alt="Ruken At A Glance"
            className="relative rounded-xl shadow-2xl w-full aspect-[4/5] object-cover"
          />
        </div>
      </div>
    </section>
  );
}

function Services() {
  return (
    <section id="services" className="section-padding bg-muted/30 leaf-bg">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14 fade-in">
          <Editable field="whatwedo.overline" as="p" className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>What We Do</Editable>
          <Editable field="whatwedo.headline" as="h2" className="font-display text-3xl md:text-5xl font-bold mt-2" style={{ color: "var(--brand-700)" }}>End-to-End Commodity Supply Solutions</Editable>
          <Editable field="whatwedo.intro" as="p" className="mt-5 text-muted-foreground leading-relaxed text-left md:text-center" multiline>
            {whatWeDoIntro}
          </Editable>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.Box;
            return (
              <div key={i} className="group bg-white border border-border rounded-xl p-8 card-hover fade-in">
                <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-5" style={{ background: "var(--brand-100)" }}>
                  <Icon size={26} style={{ color: "var(--brand-700)" }} />
                </div>
                <h3 className="font-display text-xl font-bold mb-3" style={{ color: "var(--brand-700)" }}><Editable field={`services.${i}.title`} as="span">{s.title}</Editable></h3>
                <p className="text-sm text-muted-foreground leading-relaxed"><Editable field={`services.${i}.body`} as="span" multiline>{s.body}</Editable></p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SevenPillars() {
  return (
    <section className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14 fade-in">
          <Editable field="pillars.overline" as="p" className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>How We Add Value</Editable>
          <Editable field="pillars.headline" as="h2" className="font-display text-3xl md:text-5xl font-bold mt-2 leading-tight" style={{ color: "var(--brand-700)" }} multiline>
            Seven pillars of a reputed global trading firm.
          </Editable>
          <Editable field="pillars.intro" as="p" className="mt-5 text-muted-foreground leading-relaxed" multiline>
            Supported by a worldwide network of strategically located sea ports and strong relationships with reliable manufacturers and suppliers.
          </Editable>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {strengths.map((s, i) => {
            const Icon = Icons[s.icon] || Icons.CheckCircle2;
            return (
              <div key={s.title} className="group rounded-xl overflow-hidden border border-border bg-white card-hover fade-in flex flex-col">
                <div className="aspect-[16/9] overflow-hidden bg-muted">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}>
                      <Icon size={20} style={{ color: "var(--brand-700)" }} />
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
                      Pillar {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-lg" style={{ color: "var(--brand-700)" }}><Editable field={`strengths.${i}.title`} as="span">{s.title}</Editable></h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed"><Editable field={`strengths.${i}.body`} as="span" multiline>{s.body}</Editable></p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Stats({ onCountriesClick }) {
  const iconMap = { Globe2: Icons.Globe2, Ship: Icons.Ship, Clock: Icons.Clock, BarChart3: Icons.BarChart3 };
  return (
    <section className="py-16 md:py-24 relative overflow-hidden" style={{ background: "linear-gradient(180deg, var(--brand-50) 0%, #ffffff 100%)" }}>
      {/* subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, var(--brand-700) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-12 fade-in">
          <Editable field="facts.overline" as="p" className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>Our Impact</Editable>
          <Editable field="facts.headline" as="h2" className="font-display text-3xl md:text-5xl font-bold mt-2" style={{ color: "var(--brand-700)" }}>Facts & Figures</Editable>
          <div className="mt-4 mx-auto w-16 h-1 rounded-full" style={{ background: "var(--brand-500)" }} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {glanceStats.map((s, i) => {
            const Ico = iconMap[s.icon] || Icons.Award;
            return (
              <div
                key={i}
                className="group relative bg-white rounded-2xl border border-border p-7 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 fade-in"
                data-testid={`fact-card-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="absolute top-0 left-7 right-7 h-1 rounded-b-full" style={{ background: "linear-gradient(90deg, var(--brand-500), var(--brand-700))" }} />
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110" style={{ background: "var(--brand-50)", border: "1px solid var(--brand-100)" }}>
                  <Ico size={26} style={{ color: "var(--brand-700)" }} />
                </div>
                <div className="flex items-baseline gap-1 font-display font-bold leading-none" style={{ color: "var(--brand-700)" }}>
                  <StatCounter value={s.value} suffix={s.suffix} label="" decimals={s.value % 1 !== 0 ? 1 : 0} inline />
                </div>
                <p className="mt-3 text-sm font-semibold uppercase tracking-wider text-foreground/70"><Editable field={`facts.${i}.label`} as="span">{s.label}</Editable></p>
                {s.key === "activeCountries" && (
                  <button
                    onClick={onCountriesClick}
                    className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold link-underline"
                    style={{ color: "var(--brand-700)" }}
                    data-testid="learn-more-countries"
                  >
                    Learn More <Icons.ArrowRight size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function LatestNewsSection() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/articles", { params: { limit: 3 } })
      .then(({ data }) => setItems((data.items || []).slice(0, 3)))
      .catch(() => setItems([]));
  }, []);

  // Auto-hide when no published articles yet
  if (!items || items.length === 0) return null;

  return (
    <section className="section-padding bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4 fade-in">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>News & Insights</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-2" style={{ color: "var(--brand-700)" }}>Latest News & Events</h2>
          </div>
          <Link to="/news" className="inline-flex items-center gap-2 font-semibold link-underline" style={{ color: "var(--brand-700)" }}>
            View All <Icons.ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-7">
          {items.map((n) => (
            <Link key={n.id} to={`/news/${n.slug}`} className="group bg-white rounded-xl overflow-hidden border border-border card-hover fade-in">
              {n.image && (
                <div className="aspect-[16/10] overflow-hidden bg-muted">
                  <img src={n.image} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider" style={{ background: n.type === "event" ? "#fef3c7" : "var(--brand-100)", color: n.type === "event" ? "#92400e" : "var(--brand-700)" }}>
                    {n.type === "event" ? <Icons.Calendar size={10} /> : <Icons.Newspaper size={10} />}{n.type}
                  </span>
                  <span className="inline-flex items-center gap-1"><Icons.Calendar size={12} /> {new Date(n.event_date || n.published_at).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" })}</span>
                </div>
                <h3 className="font-display text-lg font-semibold leading-snug group-hover:opacity-80" style={{ color: "var(--brand-700)" }}>{n.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function QueryForm() {
  return (
    <section className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
        <div className="fade-in">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>Send us your query</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold mt-2 leading-tight" style={{ color: "var(--brand-700)" }}>
            Need help? Reach out to us.
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            Share your product specs and we'll get back to you with the most competitive quote and the fastest delivery plan.
          </p>
          <ul className="mt-8 space-y-5">
            <li className="flex gap-4">
              <span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}>
                <Icons.MapPin size={18} style={{ color: "var(--brand-700)" }} />
              </span>
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Office</p><p className="font-medium text-foreground">{company.address}</p></div>
            </li>
            <li className="flex gap-4">
              <span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}>
                <Icons.Phone size={18} style={{ color: "var(--brand-700)" }} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Phone</p>
                <p className="font-medium text-foreground">{company.phone}</p>
                {company.landline && (
                  <p className="text-sm text-foreground/70 mt-0.5">Landline: {company.landline}</p>
                )}
              </div>
            </li>
            <li className="flex gap-4">
              <span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}>
                <Icons.Mail size={18} style={{ color: "var(--brand-700)" }} />
              </span>
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Email</p><p className="font-medium text-foreground">{company.email}</p></div>
            </li>
            <li className="flex gap-4">
              <span className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-100)" }}>
                <Icons.Clock size={18} style={{ color: "var(--brand-700)" }} />
              </span>
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Working Hours</p><p className="font-medium text-foreground">{company.hours}</p></div>
            </li>
          </ul>
        </div>
        <div className="fade-in">
          <QuoteForm />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [countriesOpen, setCountriesOpen] = useState(false);
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Ruken Alazdehar General Trading LLC",
      url: "https://rukenalazdehar.ae/",
      logo: "https://rukenalazdehar.ae/logo.png",
      foundingDate: "2020",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Business Village A, Office No. 658, Port Saeed, Al Maktoum Road",
        postalCode: "186885",
        addressLocality: "Dubai",
        addressCountry: "AE",
      },
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+971-56-3898675",
        email: "info@rukenalazdehar.ae",
        contactType: "sales",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Ruken Alazdehar General Trading LLC",
      url: "https://rukenalazdehar.ae/",
    },
  ];
  return (
    <>
      <SEO
        title="Global Network — Local Delivery"
        description="Ruken Alazdehar General Trading LLC — Dubai-based global trading house for strategic sourcing and distribution of Clinker, Limestone, Gypsum, Slag, Coal, Minerals, Chemicals and Petroleum products worldwide."
        path="/"
        jsonLd={jsonLd}
      />
      <HeroCarousel />
      <GlanceSection onLearnMore={() => setCountriesOpen(true)} />
      <MaterialHub />
      <Services />
      <CategoryGrid />
      <SevenPillars />
      <Stats onCountriesClick={() => setCountriesOpen(true)} />
      <PartnerMarquee />
      {NEWS_ENABLED && <LatestNewsSection />}
      <CallToActionBanner />
      <QueryForm />
      <CountriesModal open={countriesOpen} onOpenChange={setCountriesOpen} />
    </>
  );
}
